import { forwardRef, useCallback } from 'react'

import { cn } from '@/lib/utils'

import { FxIcon } from './FxIcon'
import { FxStatusLabel, type FxStatus } from './FxStatusLabel'

/**
 * The Dokumentenliste: a real `<table>` with `fill` header pills and 34px
 * rows, ported from the design system's `DocumentTable`.
 *
 * Two things go beyond the design system's own contract, both because the
 * cockpit artboard (`.work/zieldesign/project/Cockpit.dc.html`) needs them
 * and a second table would otherwise have to be written for it:
 *
 * - `columns` chooses which of the nine columns are drawn. The default is
 *   the design system's six; the cockpit adds the select box, the
 *   recognition stage and the matched layout.
 * - a row carries an `id`, a `selected` flag and an `href`, so the caller
 *   can hand over real documents instead of display strings.
 *
 * Passing only `rows` still renders exactly what the design system's preview
 * renders, so the design system's prop shape keeps working.
 *
 * The status cell is `FxStatusLabel`, so "geprüft" is a reviewed `done`
 * rather than a fifth status — the same model that component uses.
 * `renderStatus` replaces the cell for a screen that needs something else.
 */

export type FxDocumentColumn =
  | 'select'
  | 'name'
  | 'date'
  | 'status'
  | 'stage'
  | 'layout'
  | 'unclear'
  | 'action'
  | 'delete'

export interface FxDocumentRow {
  /** Stable key and the value handed back to every callback. */
  id?: string
  name: string
  date: string
  status: FxStatus
  /** A person has confirmed the unclear fields: the word becomes "geprüft". */
  reviewed?: boolean
  /** `null` while the document has not been processed — the cell reads "–". */
  unclear?: number | null
  /** The recognition stage a value was delivered at: "OCR", "KI-Bild", … */
  stage?: string
  stageTone?: 'neutral' | 'ai' | 'reviewed'
  /** The template layout the scan matched ("2018"), "–" when none did. */
  layout?: string
  selected?: boolean
  /** Where the row's action leads. Without it the action is a button. */
  href?: string
}

export interface FxDocumentTableProps
  extends Omit<React.TableHTMLAttributes<HTMLTableElement>, 'onSelect'> {
  rows: FxDocumentRow[]
  columns?: FxDocumentColumn[]
  /** Overrides a single header label, e.g. `{ action: 'Aktion' }`. */
  columnLabels?: Partial<Record<FxDocumentColumn, string>>
  /** The 8px column gap the cockpit uses once it shows eight columns. */
  dense?: boolean
  onOpen?: (row: FxDocumentRow) => void
  onDelete?: (row: FxDocumentRow) => void
  onToggleRow?: (row: FxDocumentRow, selected: boolean) => void
  onToggleAll?: (selected: boolean) => void
  renderStatus?: (row: FxDocumentRow) => React.ReactNode
  /** Named for screen readers; visually hidden, as the panel carries the title. */
  caption?: string
}

/**
 * The row's own action, per status. The design system's preview writes these
 * lower case ("ansehen", "prüfen"); the cockpit artboard capitalises them,
 * and the artboard is the newer drawing.
 */
const ACTION: Record<FxStatus, { label: string; tone: string }> = {
  neu: { label: 'Ansehen', tone: 'text-text-disabled' },
  inProgress: { label: 'Ansehen', tone: 'text-text-disabled' },
  unclear: { label: 'Prüfen', tone: 'text-danger-text' },
  done: { label: 'Ansehen', tone: 'text-success-text' },
}

/** A document whose analysis has not finished cannot be opened yet. */
const PENDING: FxStatus[] = ['neu', 'inProgress']

const DEFAULT_COLUMNS: FxDocumentColumn[] = [
  'name',
  'date',
  'status',
  'unclear',
  'action',
  'delete',
]

const HEADINGS: Record<FxDocumentColumn, string> = {
  select: 'Auswahl',
  name: 'Name',
  date: 'Datum',
  status: 'Status',
  stage: 'Stufe',
  layout: 'Layout',
  unclear: 'Unklar',
  action: 'Ergebnisse prüfen',
  delete: 'Löschen',
}

const STAGE_TONE = {
  neutral: 'bg-fill',
  ai: 'bg-primary-subtle',
  reviewed: 'bg-success-subtle',
} as const

export const FxDocumentTable = forwardRef<HTMLTableElement, FxDocumentTableProps>(
  (
    {
      rows,
      columns = DEFAULT_COLUMNS,
      columnLabels,
      dense = false,
      onOpen,
      onDelete,
      onToggleRow,
      onToggleAll,
      renderStatus,
      caption,
      className,
      ...props
    },
    ref,
  ) => {
    const shows = (c: FxDocumentColumn) => columns.includes(c)
    const heading = (c: FxDocumentColumn) => columnLabels?.[c] ?? HEADINGS[c]

    const selectable = rows.filter((r) => r.selected !== undefined || onToggleRow)
    const allSelected = selectable.length > 0 && selectable.every((r) => r.selected)
    const someSelected = selectable.some((r) => r.selected)

    // `indeterminate` is a property, not an attribute, so React cannot set it
    // from JSX — the header box is a partial selection often enough to matter.
    const headerBox = useCallback(
      (el: HTMLInputElement | null) => {
        if (el) el.indeterminate = someSelected && !allSelected
      },
      [someSelected, allSelected],
    )

    return (
      <div className="overflow-x-auto">
        <table
          ref={ref}
          className={cn(
            'border-separate text-body',
            dense ? 'w-[calc(100%+16px)] -mx-2 [border-spacing:8px_0]' : 'w-[calc(100%+48px)] -mx-6 [border-spacing:24px_0]',
            className,
          )}
          {...props}
        >
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c}
                  scope="col"
                  className={cn('pb-[14px] text-left font-normal', c === 'select' && 'w-6')}
                >
                  {c === 'select' ? (
                    <span className="block py-[3px]">
                      <input
                        ref={headerBox}
                        type="checkbox"
                        aria-label="Alle auswählen"
                        className="accent-primary-strong"
                        checked={allSelected}
                        readOnly={!onToggleAll}
                        onChange={onToggleAll ? (e) => onToggleAll(e.target.checked) : undefined}
                      />
                    </span>
                  ) : (
                    <span className="block whitespace-nowrap rounded-sm bg-fill px-2 py-[3px]">
                      {heading(c)}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const a = ACTION[row.status] ?? ACTION.neu
              const pending = PENDING.includes(row.status)
              const action = (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <FxIcon name="edit" size={20} />
                  {a.label}
                </span>
              )

              return (
                <tr key={row.id ?? i}>
                  {shows('select') && (
                    <td className="h-table-row px-2">
                      <input
                        type="checkbox"
                        aria-label={`${row.name} auswählen`}
                        className="accent-primary-strong"
                        checked={!!row.selected}
                        readOnly={!onToggleRow}
                        onChange={
                          onToggleRow ? (e) => onToggleRow(row, e.target.checked) : undefined
                        }
                      />
                    </td>
                  )}

                  {shows('name') && (
                    <th
                      scope="row"
                      className="h-table-row max-w-[min(38vw,460px)] px-2 text-left font-normal"
                    >
                      {/* The name is the one cell that can be arbitrarily long
                          — a scanned batch is called
                          "Anmeldung_Pressebesucher_NEIN_Komplett_part01_p001-060.pdf"
                          where the artboard's sample is "einwilligung_011.jpg".
                          Letting it size the column pushed "Aktion" out of the
                          panel, so it truncates and keeps the full name in a
                          title. */}
                      <span className="flex items-center gap-2">
                        <FxIcon
                          name="description"
                          size={20}
                          className="shrink-0 text-primary-strong"
                        />
                        <span className="truncate" title={row.name}>
                          {row.name}
                        </span>
                      </span>
                    </th>
                  )}

                  {shows('date') && (
                    <td className="h-table-row whitespace-nowrap px-2 tabular-nums">{row.date}</td>
                  )}

                  {shows('status') && (
                    <td className="h-table-row px-2">
                      {renderStatus ? (
                        renderStatus(row)
                      ) : (
                        <FxStatusLabel status={row.status} reviewed={row.reviewed} />
                      )}
                    </td>
                  )}

                  {shows('stage') && (
                    <td className="h-table-row px-2">
                      {row.stage ? (
                        <span
                          className={cn(
                            'whitespace-nowrap rounded-sm px-2 py-0.5 text-small',
                            STAGE_TONE[row.stageTone ?? 'neutral'],
                          )}
                        >
                          {row.stage}
                        </span>
                      ) : (
                        '–'
                      )}
                    </td>
                  )}

                  {shows('layout') && (
                    <td className="h-table-row whitespace-nowrap px-2">{row.layout ?? '–'}</td>
                  )}

                  {shows('unclear') && (
                    <td
                      className={cn(
                        'h-table-row px-2 tabular-nums',
                        row.unclear != null && row.unclear > 0 && 'text-danger-text',
                      )}
                    >
                      {/* A plain count, as the artboard draws it. Zero-padding
                          turned "no open fields" into "00" and one into "01",
                          which reads as a code rather than a number. */}
                      {row.unclear == null ? '–' : row.unclear}
                    </td>
                  )}

                  {shows('action') && (
                    <td className="h-table-row px-2">
                      {row.href && !pending ? (
                        <a href={row.href} className={cn('rounded-sm', a.tone)}>
                          {action}
                        </a>
                      ) : (
                        <button
                          type="button"
                          className={cn('rounded-sm', a.tone)}
                          disabled={pending}
                          onClick={onOpen ? () => onOpen(row) : undefined}
                        >
                          {action}
                        </button>
                      )}
                    </td>
                  )}

                  {shows('delete') && (
                    <td className="h-table-row px-2">
                      <button
                        type="button"
                        aria-label={`${row.name} löschen`}
                        className="flex rounded-sm text-text-disabled hover:text-danger-text"
                        onClick={onDelete ? () => onDelete(row) : undefined}
                      >
                        <FxIcon name="delete" size={20} />
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  },
)
FxDocumentTable.displayName = 'FxDocumentTable'
